import * as React from 'react';

import { LucideAlertTriangle, LucideIcon } from 'lucide-react';

import { cn } from '../../../lib/stylingUtils';
import { Col, Row } from '../containers';
import { Empty, Loading } from '../feedback';
import { Checkbox } from '../forms';

export type TinyTableSorting = {
  column: string;
  direction: 'asc' | 'desc';
};

export type TinyTableProps<T> = {
  data: T[];
  isLoading: boolean;
  onRowClick?: (row: T) => void;
  columns: {
    key: string;
    label:
      | React.ReactNode
      | ((sorted: 'asc' | 'desc' | null) => React.ReactNode);
    icon?: LucideIcon;
    sortable?: boolean;
    align?: 'left' | 'right' | 'center';
    cell: (row: T) => React.ReactNode;
    width?:
      | number
      | 'max-content'
      | 'auto'
      | 'min-content'
      | 'fit-content'
      | (string & {});
    footer?: () => React.ReactNode;
    preventRowClick?: boolean;
  }[];
  selectable?: boolean;
  selectedKeys?: Set<React.Key>;
  onSelectionChange?: (keys: Set<React.Key>) => void;
  rowKey: (row: T) => string;
  sorting?: TinyTableSorting | null;
  onSortChange?: (sorting: TinyTableSorting) => void;
  className?: string;
  headerClassName?: string;
  rowClassName?: string;
  footerClassName?: string;
  error?: Error | null;
};

type TinyTableSortIndicatorProps = {
  direction: 'asc' | 'desc' | null;
};

const TinyTableSortIndicator = ({ direction }: TinyTableSortIndicatorProps) => (
  <div className="@TinyTableSortIndicator flex flex-col gap-0">
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      className={cn(
        'cursor-pointer',
        direction === 'asc' ? 'text-neutral' : 'text-neutral/20',
      )}
    >
      <path d="M1.5 8L5 2.5L8.5 8" fill="currentColor" />
    </svg>
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      className={cn(
        'cursor-pointer',
        direction === 'desc' ? 'text-neutral' : 'text-neutral/20',
      )}
    >
      <path d="M1.5 2L5 7.5L8.5 2" fill="currentColor" />
    </svg>
  </div>
);

export const TinyTable = <T,>({
  columns,
  data,
  rowKey,
  selectable,
  selectedKeys,
  onSelectionChange,
  isLoading,
  sorting,
  onSortChange,
  className,
  headerClassName,
  rowClassName,
  footerClassName,
  error,
  onRowClick,
}: TinyTableProps<T>) => {
  const hasFooter = React.useMemo(
    () => columns.some((column) => column.footer),
    [columns],
  );

  const isControlledSelection =
    selectable && selectedKeys != null && onSelectionChange != null;
  const currentKeys = React.useMemo(
    () => selectedKeys ?? new Set<React.Key>(),
    [selectedKeys],
  );

  const allRowKeys = React.useMemo(
    () => new Set(data.map((row) => rowKey(row) as React.Key)),
    [data, rowKey],
  );
  const { allSelected, someSelected } = React.useMemo(() => {
    if (allRowKeys.size === 0) {
      return { allSelected: false, someSelected: false };
    }

    let numSelectedOnPage = 0;
    for (const key of allRowKeys) {
      if (currentKeys.has(key)) {
        numSelectedOnPage++;
      }
    }

    return {
      allSelected: numSelectedOnPage === allRowKeys.size,
      someSelected: numSelectedOnPage > 0,
    };
  }, [allRowKeys, currentKeys]);

  const handleHeaderCheckboxChange = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      const next = new Set(currentKeys);
      allRowKeys.forEach((k) => next.delete(k));
      onSelectionChange(next);
    } else {
      const next = new Set(currentKeys);
      allRowKeys.forEach((k) => next.add(k));
      onSelectionChange(next);
    }
  };

  const handleRowCheckboxChange = (key: React.Key) => {
    if (!onSelectionChange) return;
    const next = new Set(currentKeys);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    onSelectionChange(next);
  };

  React.useEffect(() => {
    if (
      !columns.some(
        (column) => column.width === undefined || column.width === 'auto',
      )
    ) {
      console.error(
        '🔴 TinyTable: At least one column should have empty or auto width',
      );
    }
  }, [columns]);

  return (
    <>
      {isLoading && (
        <Col
          className={'h-[200px]'}
          xCenter
          yCenter
          style={{
            gridColumn: `span ${columns.length} / span ${columns.length}`,
          }}
        >
          <Loading isLoading showLabel />
        </Col>
      )}

      {!isLoading && error && (
        <Col className={'min-h-[250px]'} xCenter yCenter gap3>
          <LucideAlertTriangle className="size-15 stroke-1 text-failure" />
          <Col xCenter className={'text-center'}>
            <div className={'font-semibold text-failure'}>
              Oops! Failed to load data
            </div>
            <div className={'text-sm text-failure/50'}>
              Please, try again later
            </div>
          </Col>
        </Col>
      )}

      {!isLoading && !error && (
        <div
          className={cn(
            '@TinyTable grid max-h-full overflow-y-auto rounded-lg border border-neutral/20 text-sm',
            className,
          )}
          style={{
            gridTemplateColumns: columns
              .map((column) =>
                column.width !== undefined
                  ? typeof column.width === 'number'
                    ? `${column.width}px`
                    : column.width
                  : 'auto',
              )
              .join(' '),
          }}
        >
          {columns.map((column, i) => {
            const columnSortDir =
              sorting?.column === column.key ? sorting.direction : null;
            const isSortable = column.sortable && onSortChange;

            const handleSortClick = () => {
              if (!isSortable) return;
              onSortChange({
                column: column.key,
                direction: columnSortDir === 'asc' ? 'desc' : 'asc',
              });
            };

            return (
              <Row
                key={column.key}
                className={cn(
                  'bg-background sticky top-0 z-1 gap-2.5 border-b border-neutral/20 p-3',
                  column.align === 'right' && 'justify-end',
                  column.align === 'center' && 'justify-center',
                  isSortable && 'cursor-pointer select-none',
                  headerClassName,
                )}
                onClick={isSortable ? handleSortClick : undefined}
              >
                {i === 0 && selectable && (
                  <div
                    className="flex items-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      className={'w-auto'}
                      isSelected={
                        isControlledSelection ? allSelected : undefined
                      }
                      isIndeterminate={
                        isControlledSelection
                          ? someSelected && !allSelected
                          : false
                      }
                      onChange={
                        isControlledSelection
                          ? handleHeaderCheckboxChange
                          : undefined
                      }
                    />
                  </div>
                )}
                <Row className={'gap-1.5'}>
                  {column.icon && (
                    <column.icon className={'size-4 shrink-0 opacity-60'} />
                  )}
                  <div className={'w-full opacity-60'}>
                    {typeof column.label === 'function'
                      ? column.label(columnSortDir)
                      : column.label}
                  </div>
                </Row>
                {isSortable && (
                  <TinyTableSortIndicator direction={columnSortDir} />
                )}
              </Row>
            );
          })}

          {data.length === 0 && (
            <Empty
              className={'min-h-[250px]'}
              style={{
                gridColumn: `span ${columns.length} / span ${columns.length}`,
              }}
            />
          )}

          {data.map((row, rowIndex) => (
            <div key={rowKey(row)} className={cn('group contents')}>
              {rowIndex > 0 && (
                <div
                  className={cn(
                    'border-t border-neutral/20',
                    onRowClick && 'group-hover:bg-current/5',
                  )}
                  style={{
                    gridColumn: `span ${columns.length} / span ${columns.length}`,
                  }}
                />
              )}
              {columns.map((column, i) => (
                <Row
                  key={column.key}
                  className={cn(
                    'gap-2.5 p-3',
                    column.align === 'right' && 'justify-end',
                    column.align === 'center' && 'justify-center',
                    onRowClick && 'cursor-pointer group-hover:bg-current/5',
                    rowClassName,
                  )}
                  onClick={() => !column.preventRowClick && onRowClick?.(row)}
                >
                  {i === 0 && selectable && (
                    <div
                      className="flex items-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        className={'w-auto'}
                        isSelected={
                          isControlledSelection
                            ? currentKeys.has(rowKey(row) as React.Key)
                            : undefined
                        }
                        onChange={
                          isControlledSelection
                            ? () =>
                                handleRowCheckboxChange(
                                  rowKey(row) as React.Key,
                                )
                            : undefined
                        }
                      />
                    </div>
                  )}
                  {column.cell(row)}
                </Row>
              ))}
            </div>
          ))}

          {hasFooter &&
            columns.map((column) => (
              <Row
                key={column.key}
                className={cn(
                  'bg-background sticky bottom-0 z-1 gap-2.5 border-t border-neutral/20 p-3',
                  footerClassName,
                )}
              >
                {column.footer?.()}
              </Row>
            ))}
        </div>
      )}
    </>
  );
};
